import { Dispatch, SetStateAction, useState, useEffect, useCallback } from 'react';

type SetValue<T> = Dispatch<SetStateAction<T>>;

type Listener = () => void;

const storageListeners = new Map<string, Set<Listener>>();

const notifyListeners = (key: string) => storageListeners.get(key)?.forEach(l => l());

export const useSessionStorage = <T>(key: string, initialValue: T): [T, SetValue<T>] => {
  const readValue = useCallback((): T => {
    try {
      const jsonValue = window.sessionStorage.getItem(key);

      if (typeof initialValue === 'function') {
        return (initialValue as () => T)();
      }

      return jsonValue ? JSON.parse(jsonValue) : initialValue;
    } catch (_) {
      return initialValue;
    }
  }, [initialValue, key]);

  const [valueState, setValueState] = useState<T>(readValue);

  useEffect(() => {
    if (!storageListeners.has(key)) {
      storageListeners.set(key, new Set());
    }

    const listener: Listener = () => setValueState(readValue());

    storageListeners.get(key)!.add(listener);

    return () => {
      storageListeners.get(key)?.delete(listener);
    };
  }, [key, readValue]);

  const setValue: SetValue<T> = useCallback(
    valOrFn => {
      const newValue =
        typeof valOrFn === 'function' ? (valOrFn as (prev: T) => T)(readValue()) : valOrFn;

      newValue
        ? window.sessionStorage.setItem(key, JSON.stringify(newValue))
        : window.sessionStorage.removeItem(key);

      notifyListeners(key);
    },
    [key, readValue]
  );

  return [valueState, setValue];
};
